import find from 'lodash/find';
import lodashOrderBy from 'lodash/orderBy';
import has from 'lodash/has';
import map from 'lodash/map';
import {
  productTypes,
  countryCreditRatings,
  countryCreditRatingsRange,
  sortingTypes,
  sortingDirections,
} from './config';
import { isFakeOvernightOffer } from './OfferRow/utils';
import { MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT } from './constants';

const {
  FLEXGELD,
  TERM_DEPOSIT,
  OVERNIGHT_DEPOSIT,
  FORREIGN_CURRENCY,
  NOTICE_ACCOUNT,
} = productTypes;

export const findBankById = (banks, id) => {
  return banks.find((bank) => bank.id === id);
};

export const findCountryByCode = (countries, countryCode) => {
  return countries.find((country) => country.country_code === countryCode);
};

export const filterByTerm = (items, term) => {
  if (typeof term === 'object') {
    return items.filter(
      (item) => item.number_of_terms >= term.min && item.number_of_terms <= term.max,
    );
  }

  return items.filter((item) => item.number_of_terms === term);
};

export const filterByType = (items, type, features) =>
  items.filter((item) => {
    if (type === TERM_DEPOSIT) return item.is_overnight === false;
    if (type === OVERNIGHT_DEPOSIT && !features?.splitNoticeAccountFromOvernightOffers)
      return item.is_overnight;
    if (type === OVERNIGHT_DEPOSIT && features.splitNoticeAccountFromOvernightOffers) {
      if (features?.showSpecialNoticeAccountAsOvernight)
        return (item.is_overnight && !item.is_notice_account) || isFakeOvernightOffer(item);

      return item.is_overnight && !item.is_notice_account;
    }
    if (type === NOTICE_ACCOUNT) {
      if (features?.showSpecialNoticeAccountAsOvernight)
        return (
          item.is_notice_account &&
          item.payout_period_after_termination > MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT
        );

      return item.is_notice_account;
    }
    if (type === FLEXGELD) return item.is_flexgeld === true;
    if (type === FORREIGN_CURRENCY) return item.is_in_foreign_currency === true;

    return true;
  });

export const filterByCurrency = (items, type, currency) =>
  items.filter((item) => {
    if (type === 'foreign_currency') return item.currency !== currency;

    return item.currency === currency;
  });

export const filterForeignCurrency = (items) =>
  items.filter((item) => !item.is_in_foreign_currency);

export const filterByInvestmentAmount = (items, investmentAmount) =>
  items.filter(
    (offer) =>
      investmentAmount >= offer.min_placement_amount &&
      investmentAmount <= offer.max_placement_amount,
  );

export const filterByCountryCreditRating = (items, countryCreditRating) => {
  return items.filter(
    (item) =>
      countryCreditRatings.indexOf(item.partner_bank.country.credit_rating) <=
      countryCreditRatingsRange[countryCreditRating],
  );
};

export const filterByExcludedBanks = (items, banksToExclude) => {
  return items.filter((offer) => !banksToExclude.includes(offer.partner_bank.id));
};

export const filterByIncludedTerms = (items, termsToInclude) => {
  return items.filter((offer) => termsToInclude.includes(offer.number_of_terms));
};

export const filterByIncludedRatings = (items, ratingsToInclude) => {
  return items.filter((offer) =>
    ratingsToInclude.includes(offer.partner_bank.country.credit_rating),
  );
};

export const filterByFinanztestBestenliste = (items) => {
  return items.filter(
    (offer) =>
      has(offer, 'cms_content.finanz_test_label.value') &&
      (offer.cms_content.finanz_test_label.value.includes('Finanztest Bestenliste') ||
        offer.cms_content.finanz_test_label.value.includes(
          'Finanztest Bestenliste | Sparplan verf\u00fcgbar',
        )),
  );
};

export const filterByCountriesToShow = (items, countriesToShow) => {
  return items.filter((offer) => countriesToShow.includes(offer.partner_bank.country.id));
};

export const filterOffers = (offers, config, investmentAmount, countryCreditRating) => {
  const {
    showForeignCurrency,
    showOnlyFlexgeld,
    term,
    type,
    banksToExclude = [],
    termsToInclude = [],
    countriesToShow = [],
    showOnlyFinanztestBestenlisteProducts,
    ratingsToInclude = [],
  } = config.filter;
  const { features } = config;

  let filteredOffers = offers;

  if (config.filter.term) {
    filteredOffers = filterByTerm(filteredOffers, term);
  }

  if (type) {
    filteredOffers = filterByType(filteredOffers, type, features);
  }

  if (!showForeignCurrency) {
    filteredOffers = filterForeignCurrency(filteredOffers);
  }

  if (showOnlyFlexgeld) {
    filteredOffers = filterByType(filteredOffers, FLEXGELD);
  }

  if (investmentAmount) {
    filteredOffers = filterByInvestmentAmount(filteredOffers, investmentAmount);
  }

  if (countryCreditRating) {
    filteredOffers = filterByCountryCreditRating(filteredOffers, countryCreditRating);
  }

  if (banksToExclude.length) {
    filteredOffers = filterByExcludedBanks(filteredOffers, banksToExclude);
  }

  if (termsToInclude.length) {
    filteredOffers = filterByIncludedTerms(filteredOffers, termsToInclude);
  }

  if (ratingsToInclude.length) {
    filteredOffers = filterByIncludedRatings(filteredOffers, ratingsToInclude);
  }

  if (showOnlyFinanztestBestenlisteProducts) {
    filteredOffers = filterByFinanztestBestenliste(filteredOffers);
  }

  if (countriesToShow.length) {
    filteredOffers = filterByCountriesToShow(filteredOffers, countriesToShow);
  }

  return filteredOffers;
};

export const getCreditRatingIndex = (item) => {
  const rating = item.partner_bank.country?.credit_rating;

  return countryCreditRatings.indexOf(rating);
};

const getLowerCaseBankName = (item) => item.partner_bank.name?.toLowerCase() || '';

export const orderBy = (items, { defaultRate, order, filter }) => {
  switch (order.byKey) {
    case sortingTypes.rate: {
      return lodashOrderBy(
        items,
        [defaultRate, getCreditRatingIndex, getLowerCaseBankName],
        [order.direction, sortingDirections.asc, sortingDirections.desc], // Toggle for Bank name Z > A for equal interest rates
      );
    }

    case sortingTypes.bank: {
      return lodashOrderBy(
        items,
        [getLowerCaseBankName, defaultRate, getCreditRatingIndex],
        [order.direction, sortingDirections.desc, sortingDirections.asc],
      );
    }

    case sortingTypes.country: {
      return lodashOrderBy(
        items,
        ['partner_bank.country.name', defaultRate, getLowerCaseBankName],
        [order.direction, sortingDirections.desc, sortingDirections.asc],
      );
    }

    case sortingTypes.term: {
      // Overnight and notice account don't have concept of Term, therefore sorting should not be applied to them.
      if (
        filter?.type !== productTypes.OVERNIGHT_DEPOSIT &&
        filter?.type !== productTypes.NOTICE_ACCOUNT
      ) {
        return lodashOrderBy(
          items,
          ['number_of_terms', defaultRate, getCreditRatingIndex],
          [order.direction, sortingDirections.desc, sortingDirections.asc],
        );
      }

      return items;
    }

    case sortingTypes.noticePeriod: {
      if (filter?.type === productTypes.NOTICE_ACCOUNT) {
        return lodashOrderBy(
          items,
          ['payout_period_after_termination', defaultRate, getLowerCaseBankName],
          [order.direction, sortingDirections.desc, sortingDirections.asc],
        );
      }

      return items;
    }

    default: {
      return items;
    }
  }
};

export const slice = (items, config) => {
  return items.slice(config.filter.offset, config.filter.offset + config.filter.limit);
};

export const parseDepositOfferData = ({ partner_banks, deposits, countries }) => {
  if (
    !partner_banks ||
    !Array.isArray(partner_banks) ||
    !deposits ||
    !Array.isArray(deposits) ||
    !countries ||
    !Array.isArray(countries)
  ) {
    throw new Error('Offers endpoint data is not in a valid format!');
  }

  return map(deposits, (dp) => {
    const partnerBank = find(partner_banks, { id: dp.partner_bank.id });
    const parsedDeposit = { ...dp };
    const depositHasTaxNumber =
      parsedDeposit.required_customer_data &&
      parsedDeposit.required_customer_data.includes('TAX_NUMBER');

    parsedDeposit.tax_number_required = depositHasTaxNumber || false;

    parsedDeposit.partner_bank = {
      ...partnerBank,
      country: find(countries, { country_code: partnerBank.country }),
    };

    return parsedDeposit;
  });
};
